import {
    Layout,
    ChipLbl,
    ReadonlyChip,
    InputLbl,
    Text,
    Sep,
    Button,
    Anchor,
    ChipButton,
    TextArea,
    Input,
    PopoverContent,
    PopoverToolbar,
    Select,
} from '@root/styles/Design';
import { Observer } from 'mobx-react';
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { usePromise } from '@root/hooks/usePromise';
import { useStorage } from '@root/hooks/useStorage';
import { useToggle } from '@root/hooks/useToggle';
import { Popover } from 'react-tiny-popover';
import { OperationModel, FieldModel, styled } from 'redoc';
import { colorVar } from '@root/styles/Global';
import { OpenAPISchema } from 'redoc/typings/types';
import { CodeEditor } from './CodeEditor';
import { EndpointTesterResponse, RequestBodyExample, RequestEditorModel } from './Models';
import { IconLock, IconLockOpen } from '@tabler/icons-react';

export function RequestEditor({
    baseUrl,
    onResponse,
    operation,
}: {
    baseUrl?: string;
    onResponse?: (response: null | EndpointTesterResponse) => void;
    operation: OperationModel;
}) {
    const [model] = useState(() => new RequestEditorModel(operation, baseUrl));

    // Get query parameters from operation
    const queryFields = model.operation.parameters?.filter((p) => p.in === 'query') || [];

    const handleQueryParamsChange = (queryParams: Array<QueryParam>) => {
        model.stagedRequest.queryParams = queryParams;
    };

    const handleSend = useCallback(async () => {
        onResponse?.(null);
        try {
            onResponse?.(await model.sendRequest());
        } catch (e) {
            onResponse?.({ status: -1, body: e instanceof Error ? e.message : String(e), headers: {}, durationMs: 0 });
        }
    }, [model]);

    return (
        <Layout vert fill minW={400}>
            <Layout horz px={16} pt={4} pb={8} gap="md" justify="start">
                {!model.hasSecurity ? null : (
                    <Observer>
                        {() => <AuthenticationButton value={model.stagedRequest.token} onChange={(value) => (model.stagedRequest.token = value)} />}
                    </Observer>
                )}
                <ChipLbl text="Method">
                    <ReadonlyChip style={{ textTransform: 'uppercase' }}>{model.stagedRequest.method}</ReadonlyChip>
                </ChipLbl>
                <ChipLbl text="Path" fill>
                    <ReadonlyChip fill>{model.stagedRequest.path}</ReadonlyChip>
                </ChipLbl>
            </Layout>
            {!queryFields.length ? null : (
                <Layout pb={8} px={16}>
                    <InputLbl
                        as="div"
                        text={
                            <Text size="xs" color="text-dim">
                                Query String
                            </Text>
                        }
                    >
                        <Observer>
                            {() => (
                                <QueryParamsInput fields={queryFields} onChange={handleQueryParamsChange} value={model.stagedRequest.queryParams} />
                            )}
                        </Observer>
                    </InputLbl>
                </Layout>
            )}
            <Text size="sm" px={16} pb={4} color="text-dim">
                Request Body
            </Text>
            <Sep horz />
            <Layout noScroll fill>
                <Observer>{() => <CodeEditor value={model.stagedRequest.body} onChange={(value) => (model.stagedRequest.body = value)} />}</Observer>
            </Layout>
            <Sep horz />
            <Layout px={16} py={8} horz align="center">
                <RequestBodyExampleSelector examples={model.examples} onExampleSelected={(example) => (model.stagedRequest.body = example.value)} />
                <Button onClick={handleSend} type="primary">
                    Send Request
                </Button>
            </Layout>
        </Layout>
    );
}

function useSavedToken() {
    const { get, save } = useStorage<string>('auth-token', 'memory');
    const { resolving, result } = usePromise(get('token'), []);
    return {
        tokenLoading: resolving,
        token: result,
        updateSavedToken: (value: string) => {
            save('token', value);
        },
    };
}
export function AuthenticationButton({ value, onChange }: { value: string; onChange: (value: string) => void }) {
    const [isPopoverOpen, { toggle, setOff }] = useToggle(false);

    const { token, updateSavedToken } = useSavedToken();
    useEffect(() => {
        if (token && !value) {
            onChange(token);
        }
    }, [token]);
    const close = useCallback(() => {
        updateSavedToken(value);
        setOff();
    }, [value, setOff, updateSavedToken]);

    return (
        <Popover
            isOpen={isPopoverOpen}
            positions={['bottom', 'top', 'left', 'right']}
            onClickOutside={close}
            content={
                <PopoverContent>
                    <InputLbl text="JWT Token">
                        <TextArea
                            rows={5}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="Enter token"
                            style={{ width: 280 }}
                        />
                    </InputLbl>
                </PopoverContent>
            }
        >
            <ChipLbl text="Security">
                <ChipButton active={isPopoverOpen} onClick={toggle}>
                    {value ? <IconLock /> : <IconLockOpen />} Authorization
                </ChipButton>
            </ChipLbl>
        </Popover>
    );
}

export function QueryParamsInput(props: { fields: FieldModel[]; value: Array<QueryParam>; onChange: (value: Array<QueryParam>) => void }) {
    const { fields, value, onChange } = props;

    const paramsModel = useMemo(() => {
        const createItem = (param: QueryParam, idx: number) => ({
            value: param,
            text: param.name + (param.value ? `=${param.value}` : ''),
            key: Math.random(),
            onDelete: () => {
                const nextParams = [...value];
                nextParams.splice(idx, 1);
                onChange(nextParams);
            },
            onChange: (newValue: QueryParam) => {
                const nextParams = [...value];
                nextParams[idx] = newValue;
                onChange(nextParams);
            },
        });
        return {
            items: value.map(createItem),
            addItem: (newValue: QueryParam) => {
                onChange([...value, newValue]);
            },
        };
    }, [fields, value]);
    const defaultNewValue = useMemo(() => ({ name: fields?.[0]?.name ?? '', value: '' }), [fields]);

    return (
        <QueryParamsInputEl>
            {paramsModel.items.length === 0 ? null : (
                <Text color="text-dim" mono>
                    ?
                </Text>
            )}
            {paramsModel.items.map((props, i) => (
                <Fragment key={props.key}>
                    {i > 0 ? (
                        <Text color="text-dim" mono>
                            &amp;
                        </Text>
                    ) : null}
                    <QueryParamButton {...props} fields={fields} />
                </Fragment>
            ))}
            {!defaultNewValue.name ? null : (
                <span>
                    <QueryParamButton text="Add Param..." fields={fields} value={defaultNewValue} onChange={paramsModel.addItem} />
                </span>
            )}
        </QueryParamsInputEl>
    );
}

function RequestBodyExampleSelector(props: { examples: RequestBodyExample[]; onExampleSelected: (example: RequestBodyExample) => void }) {
    const { examples, onExampleSelected } = props;
    const [isOpen, { toggle, setOff }] = useToggle(false);
    return !examples.length ? null : (
        <Popover
            isOpen={isOpen}
            positions={['top']}
            align="start"
            onClickOutside={setOff}
            content={
                <PopoverContent>
                    <InputLbl as="div" text="Request Body Examples:">
                        <Layout vert gap="sm">
                            {examples.map((example, idx) => (
                                <Anchor key={idx} size="md" onClick={() => onExampleSelected(example)}>
                                    {example.name}
                                </Anchor>
                            ))}
                        </Layout>
                    </InputLbl>
                </PopoverContent>
            }
        >
            <Anchor size="md" onClick={toggle}>
                Load an Example...
            </Anchor>
        </Popover>
    );
}

interface QueryParam {
    name: string;
    value: string;
}

interface QueryParamButtonProps {
    text: ReactNode;
    fields: FieldModel[];
    value: QueryParam;
    onChange: (value: QueryParam) => void;
    onDelete?: () => void;
}
function QueryParamButton({ text, fields, value, onChange, onDelete }: QueryParamButtonProps) {
    const [isOpen, { toggle, setOff }] = useToggle(false);
    const [stagedValue, setStagedValue] = useState({ ...value });
    useEffect(() => {
        setStagedValue({ ...value });
    }, [isOpen]);
    const saveParam = useCallback(() => {
        onChange(stagedValue);
        setOff();
    }, [stagedValue, onChange, setOff]);
    const deleteParam = useCallback(() => {
        onDelete?.();
        setOff();
    }, [onDelete, setOff]);

    return (
        <Popover
            onClickOutside={setOff}
            isOpen={isOpen}
            positions={['bottom']}
            content={
                <PopoverContent>
                    <QueryParamEditor fields={fields} value={stagedValue} onChange={setStagedValue} />
                    <PopoverToolbar>
                        <Button onClick={saveParam}>{onDelete ? 'Update' : 'Add'}</Button>
                        {!onDelete ? null : (
                            <Button onClick={deleteParam} type="danger">
                                Delete
                            </Button>
                        )}
                    </PopoverToolbar>
                </PopoverContent>
            }
        >
            <ChipButton className={isOpen ? '--active' : ''} py={3} onClick={toggle}>
                {text}
            </ChipButton>
        </Popover>
    );
}

function QueryParamEditor(props: { fields: FieldModel[]; value: QueryParam; onChange: (value: QueryParam) => void }) {
    const { fields, value: param, onChange } = props;
    const selectedField = fields.find((f) => f.name === param.name);
    const paramSch = useMemo(() => {
        const sch = selectedField?.schema.rawSchema;
        return sch?.type !== 'array' ? sch : Array.isArray(sch.items) ? sch.items[0] : typeof sch.items === 'object' ? sch.items : null;
    }, [selectedField]);

    return (
        <Layout vert gap="md">
            <InputLbl text="Parameter Name">
                <Select w={200} value={param.name} mono onChange={(e) => onChange({ ...param, name: e.target.value })}>
                    {fields.map((field) => (
                        <option key={field.name} value={field.name}>
                            {field.name}
                        </option>
                    ))}
                </Select>
            </InputLbl>
            {!paramSch ? null : (
                <InputLbl text="Parameter Value">
                    <SchemaValueEditor
                        sch={paramSch}
                        value={param.value}
                        onChange={(value) => onChange({ ...param, value: value?.toString() ?? '' })}
                    />
                </InputLbl>
            )}
        </Layout>
    );
}

const QueryParamsInputEl = styled(Layout)`
    background-color: ${colorVar('input-readonly')};
    padding: 2px 8px;
    border-radius: 4px;
    > button {
        background-color: transparent;
        border-color: transparent;
        &:hover,
        &.--active {
            background-color: #fff;
            border: 1px solid #ccc;
        }
    }
`;

function SchemaValueEditor(props: { sch: OpenAPISchema; value: unknown; onChange: (value: unknown) => void }) {
    const { sch, value, onChange } = props;

    switch (sch.type) {
        case 'string':
            return <SchemaStringEditor sch={sch} value={value} onChange={onChange} />;
        default:
            console.warn(`Unsupported schema type: ${sch.type}`);
            return <></>;
    }
}

function SchemaStringEditor(props: { sch: OpenAPISchema; value: unknown; onChange: (value: unknown) => void }) {
    const { sch, value, onChange } = props;
    const { pattern, example, maxLength, minLength } = sch;
    const placeholder = !example ? '' : example;
    const inputProps = { placeholder, pattern, minLength, maxLength };

    return <Input mono type="text" value={value?.toString() ?? ''} onChange={(e) => onChange(e.target.value)} {...inputProps} />;
}
